import { logError } from '@/lib/sentry.lib';

import { useEffect, useState } from 'react';

import { useCoworkerProfileStore } from '@/store/coworker-preferences.store';
import { type UseCoworkerPreferencesQueryOptions, useCoworkerPreferencesQuery } from '@repo/hooks';
import { getCoworkerProfile } from '@repo/utils';

import { TELEMETRY_LOGS, logTelemetry } from '@/lib/telemetry.lib';

import { getServiceEndpoint } from '@/api/helpers.api';
import type { OrderTakingWindow } from '@/api/types/window.types';
import { applyBuCodeConfig } from '@/utils/app.utils';
import { getAppConfig } from '@/utils/window.utils';

declare const window: OrderTakingWindow;

interface UseCoworkerPreferencesResult {
    buCode: string | undefined;
    isLoading: boolean;
    isError: boolean;
}

interface UseCoworkerPreferencesOptions {
    queryOptions?: Pick<UseCoworkerPreferencesQueryOptions, 'retry' | 'retryDelay'>;
}

const logCoworkerPreferencesMissingBuCode = (): void => {
    const coworkerProfile = getCoworkerProfile();
    const preferences = coworkerProfile?.data?.coworkerPreferences;
    logTelemetry('error', TELEMETRY_LOGS.CoworkerPreferencesMissingBuCode, {
        coworkerPreferences: {
            hasCoworkerProfile: Boolean(coworkerProfile),
            hasCoworkerPreferences: Boolean(preferences),
            hasBuCode: Boolean(preferences?.buCode),
        },
    });
};

// Gate the app on buCode. The existing store/XDK/event path gets first chance;
// the REST query is only enabled while the store still has no buCode.
export const useCoworkerPreferences = (options?: UseCoworkerPreferencesOptions): UseCoworkerPreferencesResult => {
    const coworkerPreferences = useCoworkerProfileStore((state) => state.coworkerPreferences);
    const setCoworkerPreferencesValue = useCoworkerProfileStore((state) => state.setCoworkerPreferencesValue);
    const [loadedBuCode, setLoadedBuCode] = useState<string>();

    const coworkerPreferencesBasePath = getServiceEndpoint(
        getAppConfig().api.services.classicSalja,
        'coworkerPreferences'
    );
    const buCode = coworkerPreferences?.buCode;
    const { data: fallbackCoworkerPreferences, isError } = useCoworkerPreferencesQuery(coworkerPreferencesBasePath, {
        enabled: !buCode,
        ...(options?.queryOptions?.retry === undefined ? {} : { retry: options.queryOptions.retry }),
        ...(options?.queryOptions?.retryDelay === undefined ? {} : { retryDelay: options.queryOptions.retryDelay }),
    });

    useEffect(() => {
        if (!fallbackCoworkerPreferences) {
            return;
        }

        setCoworkerPreferencesValue(fallbackCoworkerPreferences);
        window.salja.orders.coworkerPreferences = fallbackCoworkerPreferences;
    }, [fallbackCoworkerPreferences, setCoworkerPreferencesValue]);

    // Apply the BU code config whenever the buCode is known (or changes). The
    // config is considered loaded only once it has been applied for the current
    // buCode, so a changing buCode re-gates rendering until its config is ready.
    useEffect(() => {
        if (!buCode) {
            return;
        }

        applyBuCodeConfig(buCode)
            .catch((error: unknown) => {
                logError(error);
            })
            .finally(() => {
                setLoadedBuCode(buCode);
            });
    }, [buCode]);

    // When all retries are exhausted without a buCode, log it and surface the
    // error page.
    useEffect(() => {
        if (buCode || !isError) {
            return;
        }

        logCoworkerPreferencesMissingBuCode();
    }, [buCode, isError]);

    return {
        buCode,
        isError: !buCode && isError,
        isLoading: !buCode || loadedBuCode !== buCode,
    };
};
